import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Navbar } from "../components/layout/Navbar";

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function SignupVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = location.state?.email || "";

  const [email, setEmail] = useState(initialEmail);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const safeEmail = email.trim();
    const safeConfirmationCode = confirmationCode.trim();

    if (!safeEmail || !safeConfirmationCode) {
      setError("Email and verification code are required.");
      return;
    }

    if (!/^[0-9]{6}$/.test(safeConfirmationCode)) {
      setError("Please enter a valid 6-digit verification code.");
      return;
    }

    if (!API_BASE_URL) {
      setError("API base URL is not configured.");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/auth/verify_user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: safeEmail, confirmationCode: safeConfirmationCode }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data?.message || data?.error || "Verification failed. Please try again.";
        throw new Error(message);
      }

      setSuccess(data?.message || "Account verified successfully! You can now log in.");

      setTimeout(() => {
        navigate("/questionnaire#login", { replace: true, state: { email: safeEmail } });
      }, 1500);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="signup-verification-page">
      <Navbar />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg mx-auto">
          <Card className="card-elevated border-0">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Verify your email</CardTitle>
              <CardDescription>
                Enter the 6-digit code we sent to your email to activate your account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="verification-form">
                <div className="space-y-2">
                  <Label htmlFor="verify-email">Email</Label>
                  <Input
                    id="verify-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="verify-code">6-digit code</Label>
                  <Input
                    id="verify-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter your code"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full h-11 disabled:opacity-60"
                  data-testid="verification-submit"
                >
                  {isSubmitting ? "Verifying..." : "Verify account"}
                </Button>

                {error && (
                  <p className="text-xs text-red-500 mt-2 text-center" data-testid="verification-error">
                    {error}
                  </p>
                )}
                {success && (
                  <p className="text-xs text-green-600 mt-2 text-center" data-testid="verification-success">
                    {success}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

